import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { useAuthStore } from '@/stores/auth.store'
import { apiClient } from '@/lib/api-client'
import { AdminLayout } from '@/components/AdminLayout'
import { CompensationStatus } from '@shared/types/entities'

interface ScoutOption {
  id: string
  email: string
  firstName: string
  lastName: string
  iban?: string
  accountHolder?: string
  bankName?: string
}

interface CompensationItem {
  id: string
  scoutId: string
  period: string
  hourlyRate: number
  hoursWorked: number
  hourlyPay: number
  registrationCount: number
  bonusPerRegistration: number
  registrationBonus: number
  totalAmount: number
  status: CompensationStatus
  calculatedAt: string
  approvedBy?: string
  approvedAt?: string
  paidAt?: string
}

function buildEpcQrPayload(iban: string, recipient: string, amount: number, reference: string): string {
  return [
    'BCD',           // Service Tag
    '002',           // Version
    '1',             // Charset (UTF-8)
    'SCT',           // Identification
    '',              // BIC (optional)
    recipient.slice(0, 70),
    iban.replace(/\s/g, ''),
    `EUR${amount.toFixed(2)}`,
    '',              // Purpose code
    reference.slice(0, 140),
    '',              // Display text
  ].join('\n')
}

export function CompensationManagementPage() {
  const { t } = useTranslation()
  const { token } = useAuthStore()
  const queryClient = useQueryClient()
  const [selectedScoutId, setSelectedScoutId] = useState<string>('')
  const [calculateScoutId, setCalculateScoutId] = useState<string>('')
  const [calculatePeriod, setCalculatePeriod] = useState<string>('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [paymentModal, setPaymentModal] = useState<{ scoutId: string; period: string; amount: number; compensationId: string } | null>(null)

  const { data: scoutsData } = useQuery({
    queryKey: ['admin', 'scouts'],
    queryFn: () => apiClient.get('/admin/scouts', token!) as Promise<{ scouts: ScoutOption[] }>,
    enabled: !!token,
  })

  const { data: compensationsData, isLoading } = useQuery({
    queryKey: ['admin', 'compensations', selectedScoutId],
    queryFn: () => apiClient.get(`/admin/compensations${selectedScoutId ? `?scoutId=${selectedScoutId}` : ''}`, token!) as Promise<{ compensations: CompensationItem[] }>,
    enabled: !!token,
  })

  const scouts: ScoutOption[] = scoutsData?.scouts || []
  const compensations: CompensationItem[] = compensationsData?.compensations || []

  const calculateMutation = useMutation({
    mutationFn: () => apiClient.post(`/compensation/scouts/${calculateScoutId}/calculate?period=${calculatePeriod}`, {}, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'compensations'] })
      setMessage({ type: 'success', text: t('admin.calculationSuccess') })
      setTimeout(() => setMessage(null), 3000)
    },
    onError: () => {
      setMessage({ type: 'error', text: t('admin.calculationFailed') })
      setTimeout(() => setMessage(null), 3000)
    },
  })

  const approveMutation = useMutation({
    mutationFn: ({ scoutId, period, compensationId }: { scoutId: string; period: string; compensationId: string }) =>
      apiClient.post(`/compensation/scouts/${scoutId}/${period}/approve?compensationId=${compensationId}`, {}, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'compensations'] })
      setMessage({ type: 'success', text: t('admin.approveSuccess') })
      setTimeout(() => setMessage(null), 3000)
    },
    onError: () => {
      setMessage({ type: 'error', text: t('admin.approveFailed') })
      setTimeout(() => setMessage(null), 3000)
    },
  })

  const markPaidMutation = useMutation({
    mutationFn: ({ scoutId, period, compensationId }: { scoutId: string; period: string; compensationId: string }) =>
      apiClient.post(`/compensation/scouts/${scoutId}/${period}/paid?compensationId=${compensationId}`, {}, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'compensations'] })
      setMessage({ type: 'success', text: t('admin.markPaidSuccess') })
      setTimeout(() => setMessage(null), 3000)
    },
    onError: () => {
      setMessage({ type: 'error', text: t('admin.markPaidFailed') })
      setTimeout(() => setMessage(null), 3000)
    },
  })

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)

  const getStatusColor = (status: CompensationStatus) => {
    switch (status) {
      case CompensationStatus.PENDING: return 'bg-yellow-100 text-yellow-800'
      case CompensationStatus.APPROVED: return 'bg-blue-100 text-blue-800'
      case CompensationStatus.PAID: return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getScoutName = (scoutId: string) => {
    const scout = scouts.find(s => s.id === scoutId)
    return scout ? `${scout.firstName} ${scout.lastName}` : scoutId.slice(0, 8)
  }

  const totalPending = compensations.filter(c => c.status === CompensationStatus.PENDING).reduce((s, c) => s + c.totalAmount, 0)
  const totalApproved = compensations.filter(c => c.status === CompensationStatus.APPROVED).reduce((s, c) => s + c.totalAmount, 0)
  const totalPaid = compensations.filter(c => c.status === CompensationStatus.PAID).reduce((s, c) => s + c.totalAmount, 0)

  const currentPeriod = (() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })()

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.compensationManagement')}</h1>
          <p className="text-gray-600">{t('admin.compensationManagementDesc')}</p>
        </div>

        {message && (
          <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* Calculate Compensation */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.calculateCompensation')}</h2>
          <div className="flex items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scout</label>
              <select
                value={calculateScoutId}
                onChange={(e) => setCalculateScoutId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md min-w-[250px]"
              >
                <option value="">{t('admin.selectScout')}</option>
                {scouts.map((scout) => (
                  <option key={scout.id} value={scout.id}>
                    {scout.firstName} {scout.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('compensation.period')}</label>
              <input
                type="month"
                value={calculatePeriod}
                onChange={(e) => setCalculatePeriod(e.target.value)}
                max={currentPeriod}
                className="px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <button
              onClick={() => calculateMutation.mutate()}
              disabled={!calculateScoutId || !calculatePeriod || calculateMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {calculateMutation.isPending ? t('common.loading') : t('admin.calculate')}
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.filterByScout')}</label>
              <select
                value={selectedScoutId}
                onChange={(e) => setSelectedScoutId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md min-w-[250px]"
              >
                <option value="">{t('admin.allScouts')}</option>
                {scouts.map((scout) => (
                  <option key={scout.id} value={scout.id}>
                    {scout.firstName} {scout.lastName} ({scout.email})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">{t('compensation.totalPending')}</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">{formatCurrency(totalPending)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">{t('admin.totalApproved')}</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{formatCurrency(totalApproved)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">{t('compensation.totalPaid')}</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalPaid)}</div>
          </div>
        </div>

        {/* Compensations Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">{t('admin.compensationOverview')}</h2>
          </div>
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="p-6 text-center text-gray-500">{t('common.loading')}</div>
            ) : compensations.length === 0 ? (
              <div className="p-6 text-center text-gray-500">{t('admin.noCompensations')}</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scout</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.period')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.hours')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.hourlyPay')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.registrations')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.bonus')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('compensation.total')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {compensations.map((comp) => (
                    <tr key={comp.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{getScoutName(comp.scoutId)}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{comp.period}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{comp.hoursWorked.toFixed(2)}h</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatCurrency(comp.hourlyPay)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{comp.registrationCount}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatCurrency(comp.registrationBonus)}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{formatCurrency(comp.totalAmount)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(comp.status)}`}>
                          {t(`compensation.status.${comp.status.toLowerCase()}`)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {comp.status === CompensationStatus.PENDING && (
                          <button
                            onClick={() => approveMutation.mutate({ scoutId: comp.scoutId, period: comp.period, compensationId: comp.id })}
                            disabled={approveMutation.isPending}
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {t('admin.approve')}
                          </button>
                        )}
                        {comp.status === CompensationStatus.APPROVED && (
                          <button
                            onClick={() => setPaymentModal({ scoutId: comp.scoutId, period: comp.period, amount: comp.totalAmount, compensationId: comp.id })}
                            className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                          >
                            {t('admin.markPaid')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Payment QR Modal */}
      {paymentModal && (() => {
        const scout = scouts.find(s => s.id === paymentModal.scoutId)
        const hasIban = scout?.iban && scout?.accountHolder
        const epcPayload = hasIban
          ? buildEpcQrPayload(scout!.iban!, scout!.accountHolder!, paymentModal.amount, `Scout ${paymentModal.period}`)
          : null

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('admin.paymentTransfer')}
              </h3>

              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('admin.recipient')}:</span>
                  <span className="font-medium">{scout?.accountHolder || getScoutName(paymentModal.scoutId)}</span>
                </div>
                {scout?.iban && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">IBAN:</span>
                    <span className="font-mono text-xs">{scout.iban}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('compensation.total')}:</span>
                  <span className="font-bold text-lg">{formatCurrency(paymentModal.amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('compensation.period')}:</span>
                  <span className="font-medium">{paymentModal.period}</span>
                </div>
              </div>

              {epcPayload ? (
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-600 mb-3">{t('admin.scanToTransfer')}</p>
                  <div className="inline-block bg-white p-3 border rounded-lg">
                    <QRCodeSVG value={epcPayload} size={200} level="M" />
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md mb-4">
                  <p className="text-sm text-amber-800">{t('admin.noIbanWarning')}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setPaymentModal(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    markPaidMutation.mutate({ scoutId: paymentModal.scoutId, period: paymentModal.period, compensationId: paymentModal.compensationId })
                    setPaymentModal(null)
                  }}
                  disabled={markPaidMutation.isPending}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {t('admin.confirmPaid')}
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </AdminLayout>
  )
}
